/* Lock screen — 4-digit PIN entry with optional WebAuthn biometric quick unlock.
   Shown until we hold a valid X-Health-Token in localStorage. */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Lock } from "@phosphor-icons/react";
import health, { setHealthToken } from "./healthApi";

const b64uToBuf = (b64u) => {
  const b64 = b64u.replace(/-/g, "+").replace(/_/g, "/") + "===".slice(0, (4 - (b64u.length % 4)) % 4);
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
};
const bufToB64u = (buf) => {
  const bin = String.fromCharCode(...new Uint8Array(buf));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export default function LockScreen({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [status, setStatus] = useState(null);      // { configured, has_webauthn }
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("verify");      // "verify" | "setup"

  useEffect(() => {
    health.get("/pin/status").then((r) => {
      setStatus(r.data);
      setMode(r.data.configured ? "verify" : "setup");
    });
  }, []);

  const submit = async () => {
    if (pin.length < 4) return;
    setBusy(true);
    try {
      const path = mode === "setup" ? "/pin/set" : "/pin/verify";
      const { data } = await health.post(path, { pin });
      setHealthToken(data.token);
      toast.success(mode === "setup" ? "PIN set — you're in" : "Unlocked");
      onUnlock();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const registerBiometric = async () => {
    try {
      const rand = new Uint8Array(32); crypto.getRandomValues(rand);
      const cred = await navigator.credentials.create({
        publicKey: {
          challenge: rand,
          rp: { name: "NeuroLog Pro" },
          user: { id: new TextEncoder().encode("owner"), name: "owner", displayName: "Owner" },
          pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
          authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required" },
          timeout: 60000,
        },
      });
      await health.post("/webauthn/register", {
        credential_id: bufToB64u(cred.rawId),
        public_key: bufToB64u(cred.response.getPublicKey ? cred.response.getPublicKey() : new ArrayBuffer(0)),
      });
      toast.success("Biometric unlock enabled");
    } catch (e) {
      toast.error("Could not register biometric — device may not support it");
    }
  };

  const biometricUnlock = async () => {
    if (!status?.has_webauthn) return registerBiometric();
    try {
      const rand = new Uint8Array(32); crypto.getRandomValues(rand);
      const cred = await navigator.credentials.get({
        publicKey: { challenge: rand, timeout: 60000, userVerification: "required" },
      });
      const { data } = await health.post("/webauthn/verify", { credential_id: bufToB64u(cred.rawId) });
      setHealthToken(data.token);
      toast.success("Unlocked via biometric");
      onUnlock();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Biometric verification failed");
    }
  };

  return (
    <div
      data-testid="nl-lock-screen"
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      style={{ background: "linear-gradient(180deg,#0B132B 0%,#050b1f 100%)" }}
    >
      <div className="w-full max-w-[420px]">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center"
               style={{ background: "var(--nl-accent-dim)", border: "1px solid var(--nl-accent)" }}>
            <Lock size={22} weight="fill" color="#48CAE4" />
          </div>
          <div>
            <div className="font-label text-[11px] tracking-widest" style={{ color: "var(--nl-accent)" }}>NEUROLOG PRO</div>
            <div className="font-display text-2xl font-black" style={{ color: "var(--nl-text)" }}>
              {mode === "setup" ? "Set your 4-digit PIN" : "Enter PIN to unlock"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mb-6">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 rounded-lg flex items-center justify-center text-3xl font-black"
              style={{
                background: "var(--nl-panel)",
                border: `1px solid ${i < pin.length ? "var(--nl-accent)" : "var(--nl-line)"}`,
                color: "var(--nl-accent)",
              }}
            >
              {i < pin.length ? "•" : ""}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "bio", "0", "back"].map((k) => (
            <button
              key={k}
              data-testid={`nl-pin-key-${k}`}
              disabled={busy}
              onClick={() => {
                if (k === "back") return setPin(pin.slice(0, -1));
                if (k === "bio") return biometricUnlock();
                if (pin.length < 4) setPin(pin + k);
              }}
              className="h-14 rounded-lg font-display text-xl font-bold transition-all active:scale-95"
              style={{
                background: "var(--nl-panel)",
                border: "1px solid var(--nl-line)",
                color: k === "bio" || k === "back" ? "var(--nl-accent)" : "var(--nl-text)",
              }}
            >
              {k === "bio" ? <Fingerprint size={22} weight="duotone" /> : k === "back" ? "←" : k}
            </button>
          ))}
        </div>

        <button
          data-testid="nl-pin-submit"
          disabled={pin.length < 4 || busy}
          onClick={submit}
          className="mt-6 w-full h-12 rounded-lg font-label text-xs tracking-widest transition-all disabled:opacity-40"
          style={{ background: "var(--nl-accent)", color: "#001018" }}
        >
          {busy ? "..." : mode === "setup" ? "SET PIN & UNLOCK" : "UNLOCK"}
        </button>

        <div className="mt-4 text-center text-[11px]" style={{ color: "var(--nl-muted)" }}>
          Private medical tracker · auto-locks after {status?.session_ttl_min || 30} min inactivity
        </div>
      </div>
    </div>
  );
}
